import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MatchingOperator } from '../../../../../../../../modules/audiences/AudienceForm/constants.ts';
import type { AudienceForm, Group } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { EnforceMarketUnionOperatorsCallOptions } from '../../../../../../../../modules/audiences/AudienceForm/utils/applyMarketForcedUnionOperators.ts';
import type { CriteriaFieldName } from '../GroupList.tsx';

export type CriteriaGroupFieldName = `${CriteriaFieldName}.groups`;

export type UseSubGroupHandlersProps = {
  fieldPath: CriteriaGroupFieldName;
  fields: Group[];
  remove: (index: number) => void;
  removeSubgroup: (lastSubgroup?: Group) => void;
  setSubgroupOperator: (operator: MatchingOperator) => void;
  parentIndex: number;
  onAfterMutation?: (options?: EnforceMarketUnionOperatorsCallOptions) => void;
};

export const useSubGroupHandlers = ({
  fieldPath,
  fields,
  remove,
  removeSubgroup,
  parentIndex,
  onAfterMutation,
}: UseSubGroupHandlersProps) => {
  const { setValue, getValues } = useFormContext<AudienceForm>();
  const runAfter = onAfterMutation ?? (() => undefined);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const fieldName = `${fieldPath}.${parentIndex}` as const;

  const handleToggleOperator = useCallback(() => {
    const currentOperator = getValues(`${fieldName}.operator`);
    const newOperator =
      currentOperator === MatchingOperator.INTERSECTION ? MatchingOperator.UNION : MatchingOperator.INTERSECTION;

    setValue(`${fieldName}.operator`, newOperator);
    runAfter({ includeSellableMultipartner: true });
  }, [fieldName, setValue, getValues, runAfter]);

  const handleOpenModal = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (selectedIndex === null) return;

    const isOnlyTwoSubgroups = fields.length === 2;

    if (isOnlyTwoSubgroups) {
      const remainingSubgroupIndex = selectedIndex === 0 ? 1 : 0;
      const remainingSubgroup = fields[remainingSubgroupIndex];

      removeSubgroup({
        ...remainingSubgroup,
        operator: MatchingOperator.UNION,
      });
      setSelectedIndex(null);
      runAfter({ includeSellableMultipartner: true });
      return;
    }

    remove(selectedIndex);
    setSelectedIndex(null);
    runAfter({ includeSellableMultipartner: true });
  }, [selectedIndex, remove, fields, removeSubgroup, runAfter]);

  const handleRemoveSubgroup = useCallback(
    (index: number, isEmpty: boolean) => {
      if (!isEmpty) {
        handleOpenModal(index);
        return;
      }

      const isLastSubgroup = fields.length === 1;
      const isOnlyTwoSubgroups = fields.length === 2;

      if (isLastSubgroup) {
        removeSubgroup();
        runAfter({ includeSellableMultipartner: true });
        return;
      }

      if (isOnlyTwoSubgroups) {
        const remainingSubgroupIndex = index === 0 ? 1 : 0;
        const remainingSubgroup = fields[remainingSubgroupIndex];

        removeSubgroup({
          ...remainingSubgroup,
          operator: MatchingOperator.UNION,
        });
        runAfter({ includeSellableMultipartner: true });
        return;
      }

      remove(index);
      runAfter({ includeSellableMultipartner: true });
    },
    [fields, removeSubgroup, remove, handleOpenModal, runAfter]
  );

  return {
    selectedIndex,
    handleToggleOperator,
    handleOpenModal,
    handleCloseModal,
    handleConfirmDelete,
    handleRemoveSubgroup,
  };
};
